import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  CircularProgress,
  Stack,
  Chip,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import { useTranslation } from 'react-i18next';
import customFetch from '../utils/axios';
import { API_ENDPOINTS } from '../config/api';
import { formatDistanceToNow } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';

const Notifications = () => {
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const locale = i18n.language === 'pt' ? ptBR : enUS;

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await customFetch.get(API_ENDPOINTS.NOTIFICATIONS.GET_NOTIFICATIONS);
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await customFetch.put(`${API_ENDPOINTS.NOTIFICATIONS.MARK_AS_READ}/${notificationId}`);
      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await customFetch.put(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_AS_READ);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await customFetch.delete(`${API_ENDPOINTS.NOTIFICATIONS.DELETE_NOTIFICATION}/${notificationId}`);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const formatDate = (date) => {
    return formatDistanceToNow(new Date(date), { addSuffix: true, locale });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <PageHeader
        title={t('sidebar.notifications', 'Notifications')}
        icon={<NotificationsIcon />}
        showAddButton={false}
      />

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="body2" color="primary">
            {unreadCount} {t('notifications.unread', 'unread')}
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllAsRead}
              startIcon={<CheckCircleIcon />}
            >
              {t('notifications.markAllAsRead', 'Mark all as read')}
            </Button>
          )}
        </Box>

        {notifications.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {t('notifications.noNotifications', 'No notifications')}
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {notifications.map((notification) => (
              <Paper
                key={notification._id}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  borderLeft: notification.read ? 'none' : '4px solid',
                  borderLeftColor: 'primary.main',
                  cursor: notification.read ? 'default' : 'pointer',
                  '&:hover': {
                    bgcolor: notification.read ? 'background.paper' : 'action.hover'
                  }
                }}
                onClick={() => !notification.read && handleMarkAsRead(notification._id)}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: notification.read ? 400 : 600 }}>
                      {notification.title}
                    </Typography>
                    {!notification.read && (
                      <Chip label="New" size="small" color="primary" sx={{ height: 20 }} />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {notification.message}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {formatDate(notification.createdAt)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(notification._id);
                  }}
                  sx={{ color: '#ff5252' }}
                >
                  <DeleteIcon />
                </IconButton>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </>
  );
};

export default Notifications;
